import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Key, Lock, XCircle, CreditCard, CheckCircle } from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { submitSupportRequest } from '../../services/api';
import './SupportPage.css';

const QUICK_ACTIONS = [
    {
        icon: Key,
        title: 'Forgot Password',
        desc: 'Reset your password',
        action: 'link' as const,
        link: '/reset-password',
    },
    {
        icon: Lock,
        title: "Can't Access Account",
        desc: 'Get help logging in',
        action: 'category' as const,
        category: 'account_access',
    },
    {
        icon: XCircle,
        title: 'Cancel Subscription',
        desc: 'Request cancellation',
        action: 'category' as const,
        category: 'cancel_subscription',
    },
    {
        icon: CreditCard,
        title: 'Billing Question',
        desc: 'Charges, invoices, plans',
        action: 'category' as const,
        category: 'billing',
    },
];

const SupportPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState('general');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState(''); // honeypot
    const [formLoadedAt] = useState(() => Math.floor(Date.now() / 1000));

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const formRef = useRef<HTMLDivElement>(null);

    const scrollToForm = (preselectedCategory?: string) => {
        if (preselectedCategory) {
            setCategory(preselectedCategory);
        }
        setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Email is required.');
            return;
        }
        if (!message.trim() || message.trim().length < 20) {
            setError('Message must be at least 20 characters.');
            return;
        }

        setIsSubmitting(true);
        try {
            await submitSupportRequest({
                email: email.trim(),
                name: name.trim() || undefined,
                category,
                subject: subject.trim() || undefined,
                message: message.trim(),
                website,
                form_loaded_at: formLoadedAt,
            });
            setSuccess(true);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(detail);
            } else {
                setError('Failed to submit request. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="support-page">
            <div className="support-header">
                <h1>How can we help?</h1>
                <p>Choose a topic below or send us a message.</p>
            </div>

            {/* Quick action cards */}
            <div className="support-quick-actions">
                {QUICK_ACTIONS.map((action) => {
                    const Icon = action.icon;
                    if (action.action === 'link') {
                        return (
                            <Link
                                key={action.title}
                                to={action.link!}
                                className="support-action-card"
                            >
                                <div className="support-action-icon">
                                    <Icon size={20} />
                                </div>
                                <span className="support-action-title">{action.title}</span>
                                <span className="support-action-desc">{action.desc}</span>
                            </Link>
                        );
                    }
                    return (
                        <div
                            key={action.title}
                            className="support-action-card"
                            onClick={() => scrollToForm(action.category)}
                        >
                            <div className="support-action-icon">
                                <Icon size={20} />
                            </div>
                            <span className="support-action-title">{action.title}</span>
                            <span className="support-action-desc">{action.desc}</span>
                        </div>
                    );
                })}
            </div>

            {/* Contact form */}
            <div className="support-form-container" ref={formRef}>
                <GlassCard variant="strong" padding="lg">
                    {success ? (
                        <div className="support-success">
                            <div className="support-success-icon">
                                <CheckCircle size={40} />
                            </div>
                            <h3>Request received</h3>
                            <p>We'll respond within 24 hours.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="support-form">
                            <h2 className="support-form-title">Contact Support</h2>
                            <p className="support-form-subtitle">
                                Fill out the form below and we'll get back to you as soon as possible.
                            </p>

                            {error && <div className="support-error">{error}</div>}

                            <GlassInput
                                label="Email"
                                type="email"
                                value={email}
                                onChange={setEmail}
                                placeholder="your@email.com"
                                disabled={isSubmitting}
                            />

                            <GlassInput
                                label="Name (optional)"
                                type="text"
                                value={name}
                                onChange={setName}
                                placeholder="Your name"
                                disabled={isSubmitting}
                            />

                            <div className="support-select-group">
                                <label>Category</label>
                                <select
                                    className="support-select"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    disabled={isSubmitting}
                                >
                                    <option value="general">General enquiry</option>
                                    <option value="account_access">I can't access my account</option>
                                    <option value="cancel_subscription">I want to cancel my subscription</option>
                                    <option value="billing">I have a billing question</option>
                                </select>
                            </div>

                            <GlassInput
                                label="Subject (optional)"
                                type="text"
                                value={subject}
                                onChange={setSubject}
                                placeholder="Brief summary"
                                disabled={isSubmitting}
                            />

                            <div className="support-textarea-group">
                                <label>Message</label>
                                <textarea
                                    className="support-textarea"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Describe your issue in detail (min 20 characters)..."
                                    disabled={isSubmitting}
                                    rows={5}
                                />
                            </div>

                            {/* Honeypot field -- hidden from real users */}
                            <input
                                name="website"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                style={{ display: 'none' }}
                                tabIndex={-1}
                                autoComplete="off"
                            />

                            <GlassButton
                                type="submit"
                                variant="primary"
                                size="lg"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                                className="auth-submit-btn"
                            >
                                Send Request
                            </GlassButton>
                        </form>
                    )}
                </GlassCard>
            </div>

            <p className="support-footer-note">
                For urgent security concerns, email{' '}
                <a href="mailto:security@clearbyte.au">security@clearbyte.au</a> directly.
            </p>

            <div className="support-back-link">
                <Link to="/login">Back to Sign In</Link>
            </div>
        </div>
    );
};

export default SupportPage;
